import type { Vec2, Vec3 } from '../../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import { orientedFace, planPoint } from '../../solids';

/** Segments of a round arch from springing to springing; even, so one point is the crown. */
export const ARCH_SEGMENTS = 12;

/** A block along u with a round-arched passage through it, open underfoot. */
export interface ArchedBlock {
  readonly u: readonly [number, number];
  readonly v: readonly [number, number];
  readonly foot: number;
  readonly top: number;
  readonly passage: { readonly v: readonly [number, number]; readonly springing: number };
  /** Left open where the wall goes on above in another material, its sides meeting this block's. */
  readonly openTop?: boolean;
}

/** A flank laid in another material than the rest of the block, at v[0] or v[1]. */
export type BlockFlanks = Partial<Readonly<Record<'v0' | 'v1', VertexStyle>>>;

type Section = readonly [number, number];

function archPoints(block: ArchedBlock): Section[] {
  const [p0, p1] = block.passage.v;
  const r = (p1 - p0) / 2;
  return Array.from({ length: ARCH_SEGMENTS + 1 }, (_, k): Section => {
    const a = (Math.PI * k) / ARCH_SEGMENTS;
    return [(p0 + p1) / 2 + r * Math.cos(a), block.passage.springing + r * Math.sin(a)];
  });
}

function checked(block: ArchedBlock): ArchedBlock {
  const [p0, p1] = block.passage.v;
  const crown = block.passage.springing + (p1 - p0) / 2;
  if (!(
    block.v[0] < p0 &&
    p1 < block.v[1] &&
    crown < block.top &&
    block.foot < block.passage.springing
  )) {
    throw new RangeError(
      'An arched block needs its passage inside it, with masonry over the crown'
    );
  }
  return block;
}

/* Counter-clockwise in (v, y): along the foot, up and over the arch, down, and round the top. */
function sectionRing(block: ArchedBlock): Section[] {
  const { v, foot, top, passage } = block;
  return [
    [v[0], foot],
    [passage.v[0], foot],
    ...archPoints(block).reverse(),
    [passage.v[1], foot],
    [v[1], foot],
    [v[1], top],
    [v[0], top],
  ];
}

function fan(apex: Section, rim: readonly Section[]): Section[][] {
  return rim.slice(1).map((q, i) => [apex, rim[i]!, q]);
}

/*
 * Each half of an end fans from its top corner, which stands outside and above every point of its
 * half of the arch, so no triangle comes out a sliver (as in the wall arcade).
 */
function endTriangles(block: ArchedBlock): Section[][] {
  const arc = archPoints(block);
  const half = ARCH_SEGMENTS / 2;
  const left: Section = [block.v[0], block.top];
  const right: Section = [block.v[1], block.top];
  const leftRim: Section[] = [
    [block.v[0], block.foot],
    [block.passage.v[0], block.foot],
    ...arc.slice(half).reverse(),
  ];
  const rightRim: Section[] = [
    ...arc.slice(0, half + 1).reverse(),
    [block.passage.v[1], block.foot],
    [block.v[1], block.foot],
  ];
  return [...fan(left, leftRim), [left, arc[half]!, right], ...fan(right, rightRim)];
}

function sectionStyle(
  block: ArchedBlock,
  p: Section,
  q: Section,
  style: VertexStyle,
  flanks: BlockFlanks
): VertexStyle | undefined {
  if (p[1] === block.foot && q[1] === block.foot) return undefined;
  if (p[1] === block.top && q[1] === block.top) return block.openTop ? undefined : style;
  if (p[0] === block.v[0] && q[0] === block.v[0]) return flanks.v0 ?? style;
  if (p[0] === block.v[1] && q[0] === block.v[1]) return flanks.v1 ?? style;
  return style;
}

/** A gate block with its passage: side walls, vault, top and both ends, and no floor. */
export function archedBlock(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  spec: ArchedBlock,
  flanks: BlockFlanks = {}
): void {
  const block = checked(spec);
  const ring = sectionRing(block);
  const [u0, u1] = block.u;
  ring.forEach((p, i) => {
    const q = ring[(i + 1) % ring.length]!;
    const faceStyle = sectionStyle(block, p, q, style, flanks);
    if (!faceStyle) return;
    const quad = [
      planPoint(u0, p[1], p[0]),
      planPoint(u1, p[1], p[0]),
      planPoint(u1, q[1], q[0]),
      planPoint(u0, q[1], q[0]),
    ];
    orientedFace(builder, faceStyle, frame, quad, [0, -(q[0] - p[0]), -(q[1] - p[1])]);
  });
  for (const [u, sign] of [
    [u0, -1],
    [u1, 1],
  ] as const) {
    for (const tri of endTriangles(block)) {
      orientedFace(
        builder,
        style,
        frame,
        tri.map((s) => planPoint(u, s[1], s[0])),
        [sign, 0, 0]
      );
    }
  }
}

/** A window or loophole: a dark panel standing `proud` of a wall point, `depth` thick in all. */
export interface PanelSpec {
  readonly at: Vec2;
  /** Plan angle of the wall's outward normal there, counter-clockwise from u. */
  readonly facing: number;
  readonly width: number;
  readonly sill: number;
  /** Top of the panel's straight sides; a round head rises half its width above. */
  readonly head: number;
  readonly arch: boolean;
  readonly proud: number;
  readonly depth: number;
}

/* Counter-clockwise as seen from outside, in (s to the viewer's right, y). */
function panelOutline(spec: PanelSpec): Section[] {
  const w = spec.width / 2;
  const head: Section[] = spec.arch
    ? Array.from({ length: 7 }, (_, k): Section => {
        const a = (Math.PI * k) / 6;
        return [w * Math.cos(a), spec.head + w * Math.sin(a)];
      })
    : [
        [w, spec.head],
        [-w, spec.head],
      ];
  return [[-w, spec.sill], [w, spec.sill], ...head];
}

export function panel(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  spec: PanelSpec
): void {
  const n: Vec2 = [Math.cos(spec.facing), Math.sin(spec.facing)];
  const t: Vec2 = [-n[1], n[0]];
  const at = (s: Section, d: number): Vec3 =>
    planPoint(spec.at[0] + t[0] * s[0] + n[0] * d, s[1], spec.at[1] + t[1] * s[0] + n[1] * d);
  const outline = panelOutline(spec);
  const [front, back] = [spec.proud, spec.proud - spec.depth];
  orientedFace(
    builder,
    style,
    frame,
    outline.map((s) => at(s, front)),
    [n[0], 0, -n[1]]
  );
  orientedFace(
    builder,
    style,
    frame,
    outline.map((s) => at(s, back)),
    [-n[0], 0, n[1]]
  );
  outline.forEach((p, i) => {
    const q = outline[(i + 1) % outline.length]!;
    const [ds, dy] = [q[0] - p[0], q[1] - p[1]];
    const side = [at(p, back), at(q, back), at(q, front), at(p, front)];
    orientedFace(builder, style, frame, side, [t[0] * dy, -ds, -t[1] * dy]);
  });
}

/** A roof with a gable at u0 and a hip at u1 over the rectangle u × v, rising to a ridge on the middle of v. */
export interface GableHipRoofSpec {
  readonly u: readonly [number, number];
  readonly v: readonly [number, number];
  readonly eave: number;
  readonly ridge: number;
}

/* The hip rises at the pitch of the sides, so the ridge ends half the roof's width short of u1. */
export function gableHipRoof(
  builder: MeshBuilder,
  styles: { readonly roof: VertexStyle; readonly wall: VertexStyle },
  frame: Frame,
  spec: GableHipRoofSpec
): void {
  const [u0, u1] = spec.u;
  const [v0, v1] = spec.v;
  const vm = (v0 + v1) / 2;
  const uh = u1 - (v1 - v0) / 2;
  if (!(uh > u0))
    throw new RangeError('A gable-and-hip roof needs a ridge: make it longer than it is wide');
  const eave = (u: number, v: number): Vec3 => planPoint(u, spec.eave, v);
  const ridge = (u: number): Vec3 => planPoint(u, spec.ridge, vm);
  const { roof, wall } = styles;
  orientedFace(builder, roof, frame, [eave(u0, v0), eave(u1, v0), ridge(uh), ridge(u0)], [0, 1, 1]);
  orientedFace(
    builder,
    roof,
    frame,
    [eave(u0, v1), ridge(u0), ridge(uh), eave(u1, v1)],
    [0, 1, -1]
  );
  orientedFace(builder, roof, frame, [eave(u1, v0), eave(u1, v1), ridge(uh)], [1, 1, 0]);
  orientedFace(builder, wall, frame, [eave(u0, v0), ridge(u0), eave(u0, v1)], [-1, 0, 0]);
}
